import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiArrowUpRight, FiPlus, FiChevronDown, FiChevronUp, FiSearch, FiBarChart2, FiZap, FiTarget } from "react-icons/fi";
import { openConsultationMail } from "../config/contact";
import { Helmet } from "react-helmet-async";
import ServiceDetailSection from "../components/ServiceDetailSection";
import ServiceIntroSection from "../components/ServiceIntroSection";
import ServicePageHero from "../components/ServicePageHero";
import img_blog_post_amico from "../assets/Blog post-amico.png";
import img_digital_tools_amico_1 from "../assets/Digital tools-amico (1).png";
import img_generating_new_leads_amico from "../assets/Generating new leads-amico.png";
import img_multi_device_targeting_amico from "../assets/Multi-device targeting-amico.png";
import img_seo_analytics_team_amico from "../assets/SEO analytics team-amico.png";
import img_seo_analytics_team_cuate from "../assets/SEO analytics team-cuate.png";
import img_search_amico from "../assets/Search-amico.png";
import img_about_img3 from "../assets/Choose-bro.png";
import img_about_img4 from "../assets/Starting a business project-amico.png";


const FAQ_DATA = [
  { question: "1. How long does SEO take to show results?", answer: "SEO is a long-term process, and results usually start becoming visible within 3 to 6 months. The timeline depends on factors like competition, industry, and your website's current condition." },
  { question: "2. Is SEO suitable for small businesses?", answer: "Yes, SEO is highly effective for small businesses as it helps improve online visibility and attract potential customers without relying heavily on paid ads." },
  { question: "3. What is the difference between SEO and PPC?", answer: "SEO focuses on improving organic rankings over time, while PPC generates instant traffic through paid ads. Both can work together for better results." },
  { question: "4. Do you provide local SEO services in Delhi NCR?", answer: "Yes, we offer local SEO services in Delhi, Noida, and Gurugram to help your business appear in location-based searches and attract nearby customers." },
];

const WHY_ITEMS = [
  { icon: <FiTarget />, title: "Tailored SEO Approach", desc: "Every business is different, and so is our strategy. We take time to understand your goals, audience, and market before planning the right SEO approach." },
  { icon: <FiBarChart2 />, title: "Clear & Honest Communication", desc: "We believe in keeping everything simple and transparent. You will always know what work is being done and how your website is performing." },
  { icon: <FiSearch />, title: "Focus on Real Results", desc: "Our goal is not just to improve rankings but to drive meaningful outcomes — organic traffic, visibility, and quality leads." },
  { icon: <FiZap />, title: "Consistent Growth Strategy", desc: "SEO is a long-term process, and we build strategies that deliver consistent results over time with sustainable growth." },
];

const scrollReveal = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.2 }, transition: { duration: 0.6, ease: "easeOut" } };
const staggerContainer = { initial: {}, whileInView: { transition: { staggerChildren: 0.1 } } };
const itemFade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.5, ease: "easeOut" } };

const SeoServicesPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeWhy, setActiveWhy] = useState(0);
  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  useEffect(() => {
    // Fallback: Update meta tags directly
    document.title = "SEO Services Company in India | Hire SEO Experts";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = "Boost your rankings with professional SEO services. Hire a trusted SEO company in India for on-page, off-page, and technical SEO to grow your business.";
  }, []);

  return (
    <>
      <Helmet>
        <title>SEO Services Company in India | Hire SEO Experts</title>
        <meta name="description" content="Boost your rankings with professional SEO services. Hire a trusted SEO company in India for on-page, off-page, and technical SEO to grow your business." />
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "name": "PD Marketz",
                "url": "https://capable-souffle-33bc70.netlify.app",
                "logo": "https://capable-souffle-33bc70.netlify.app/logo512.png",
                "sameAs": [
                  "https://www.linkedin.com/",
                  "https://www.instagram.com/"
                ]
              },
              {
                "@type": "Service",
                "serviceType": "SEO Services",
                "provider": {
                  "@type": "Organization",
                  "name": "PD Marketz"
                },
                "areaServed": {
                  "@type": "Place",
                  "name": "India"
                },
                "description": "Professional SEO services including on-page, technical SEO, content optimization, and link building."
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "How long does SEO take to show results?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SEO is a long-term process and results typically start becoming visible within 3 to 6 months."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Is SEO suitable for small businesses?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes, SEO is highly effective for small businesses as it helps improve online visibility and attract potential customers."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What is the difference between SEO and PPC?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SEO focuses on organic rankings, while PPC generates instant traffic through paid ads."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Do you provide local SEO services?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes, we offer local SEO services to help businesses appear in location-based searches."
                    }
                  }
                ]
              }
            ]
          }
          `}
        </script>
      </Helmet>
      <main className="seo-services-page">
      <ServicePageHero
        badge="Our Services"
        title="Best SEO Services"
        subtitle="Grow your business with effective SEO strategies that drive organic traffic, improve rankings, and generate quality leads."
        breadcrumb="Home > Services > SEO"
        image={img_seo_analytics_team_amico}
      />


      {/* INTRO */}
      <ServiceIntroSection
        heading="How SEO Helps Your Business Grow"
        imageLeft={img_seo_analytics_team_cuate}
        imageRight={img_search_amico}
        paragraph1="Search Engine Optimization (SEO) is the foundation of long-term digital growth. It helps your business rank higher on Google, attract the right audience, and generate consistent organic traffic."
        paragraph2="As a leading SEO agency, we build data-driven strategies tailored to your business goals."
      />

      <ServiceDetailSection isReversed={false} label="Our SEO Strategy" title="Keyword Research & Strategy"
        description="We identify high-intent keywords that your target audience is actively searching for. Our goal is to understand user intent and find the right opportunities that align with your business."
        longDescription="Based on this, we create a keyword strategy that helps improve visibility, attract relevant traffic, and support long-term growth."
        image={img_digital_tools_amico_1}
      />
      <ServiceDetailSection isReversed={true} bgLight title="On-Page & Technical SEO"
        description="We optimize your website's content, structure, and key elements like meta tags, headings, and internal linking. This ensures your website is well-structured and search engine friendly."
        longDescription="At the same time, we improve technical aspects such as website speed, mobile responsiveness, and indexing to enhance overall performance."
        image={img_blog_post_amico}
      />
      <ServiceDetailSection isReversed={false} title="Content Optimization"
        description="We create and optimize content that is informative, engaging, and aligned with user intent. Our focus is on delivering value while keeping content relevant and easy to understand."
        longDescription="This helps improve search rankings, increase engagement, and drive consistent organic traffic to your website."
        image={img_generating_new_leads_amico}
      />
      <ServiceDetailSection isReversed={true} bgLight title="Off-Page SEO & Link Building"
        description="We focus on building high-quality backlinks from relevant and trusted sources. This helps improve your website's authority and credibility in search engines."
        longDescription="With the right link-building strategy, we strengthen your online presence and improve rankings over time."
        image={img_multi_device_targeting_amico}
      />
      <ServiceDetailSection isReversed={false} title="Optimizing for AI-Driven Search"
        description="We optimize your content not just for traditional search engines but also for AI-driven search experiences like Google AI Overviews and other generative platforms."
        longDescription="Our approach focuses on creating structured, high-quality content that is more likely to be featured in AI-generated answers."
        image={img_generating_new_leads_amico}
      />

      {/* WHY CHOOSE US */}
      <section className="dark-why-section">
        <div className="container-custom">
          <motion.div className="dark-why-header" {...scrollReveal}>
            <span className="dark-why-badge">Why Choose Us</span>
            <h2>Why Businesses Trust Our SEO Expertise</h2>
          </motion.div>
          <motion.div className="dark-why-grid" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{ once: true, amount: 0.1 }}>
            <motion.div className="dark-why-list" variants={itemFade}>
              {WHY_ITEMS.map((item, idx) => (
                <div key={idx} className={`dark-why-item ${activeWhy === idx ? "active" : ""}`} onMouseEnter={() => setActiveWhy(idx)}>
                  <div className="dark-why-item-header">
                    <span className="dark-why-item-icon-left">{item.icon}</span>
                    <h4>{idx + 1}. {item.title}</h4>
                    <span className="dark-why-item-icon">{activeWhy === idx ? <FiArrowUpRight /> : <FiPlus />}</span>
                  </div>
                  <div className="dark-why-item-content"><p>{item.desc}</p></div>
                </div>
              ))}
            </motion.div>
            <motion.div className="dark-why-visual" variants={itemFade}>
              <img src={img_about_img3} alt="Expertise 1" className="dark-oval-1" />
              <img src={img_about_img4} alt="Expertise 2" className="dark-oval-2" />
              <span className="dark-deco-plus"><FiPlus /></span>
              <span className="dark-deco-star">✷</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section py-20">
        <div className="container-custom max-w-4xl">
          <motion.h2 className="section-title text-center mb-16" {...scrollReveal}>Frequently Asked Questions</motion.h2>
          <motion.div className="faq-list" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{ once: true, amount: 0.1 }}>
            {FAQ_DATA.map((faq, i) => (
              <motion.div key={i} className={`faq-item ${openFaq === i ? "active" : ""}`} variants={itemFade}>
                <button className="faq-question" onClick={() => toggleFaq(i)}>
                  <span>{faq.question}</span>
                  {openFaq === i ? <FiChevronUp /> : <FiChevronDown />}
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div className="faq-answer" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                      <p>{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="service-page-cta service-page-cta--soft py-20">
        <div className="container-custom">
          <motion.div className="service-page-cta-inner text-center" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2>Let's Turn Your Website Into a Growth Channel</h2>
            <p className="mb-8">We help improve your search ranking, drive quality traffic, and generate real leads through effective SEO strategies.</p>
            <button className="svc-cta-btn" onClick={openConsultationMail}>
              Get Free Consultation <FiArrowUpRight />
            </button>
          </motion.div>
        </div>
      </section>
      </main>
    </>
  );
};

export default SeoServicesPage;
